import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    component: () => import('@/views/AppShell.vue'),
    children: [
      { path: '', redirect: { name: 'wishlist' } },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue')
      },
      {
        path: 'wishlist',
        name: 'wishlist',
        component: () => import('@/views/WishlistView.vue')
      },
      {
        path: 'catalog',
        name: 'catalog',
        component: () => import('@/views/CatalogView.vue')
      },
      {
        path: 'admin/manufacturer',
        name: 'manufacturer-list',
        component: () => import('@/views/ManufacturerListView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/manufacturer/new',
        name: 'manufacturer-new',
        component: () => import('@/views/ManufacturerFormView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/manufacturer/:code',
        name: 'manufacturer-detail',
        component: () => import('@/views/ManufacturerDetailView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/manufacturer/:code/edit',
        name: 'manufacturer-edit',
        component: () => import('@/views/ManufacturerFormView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/user',
        name: 'user-list',
        component: () => import('@/views/UserListView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/user/new',
        name: 'user-new',
        component: () => import('@/views/UserFormView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/user/:id/edit',
        name: 'user-edit',
        component: () => import('@/views/UserFormView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/sparepart',
        name: 'sparepart-list',
        component: () => import('@/views/SparePartListView.vue'),
        meta: { requiresManager: true }
      },
      {
        path: 'admin/sparepart/new',
        name: 'sparepart-new',
        component: () => import('@/views/SparePartFormView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/sparepart/:id/edit',
        name: 'sparepart-edit',
        component: () => import('@/views/SparePartFormView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/import',
        name: 'import-list',
        component: () => import('@/views/ImportListView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/import/new',
        name: 'import-new',
        component: () => import('@/views/ImportWizardView.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/import/:id',
        name: 'import-detail',
        component: () => import('@/views/ImportDetailView.vue'),
        meta: { requiresAdmin: true }
      },
      /*
       * Catchall fuer 404 — innerhalb der AppShell, damit Sidebar +
       * Header sichtbar bleiben und der User von der 404-Page direkt
       * weiter navigieren kann. Muss als LETZTES Kind stehen, sonst
       * fangen andere Routen nicht mehr. {pathMatch(.*)*} matched
       * beliebige Tiefe inkl. leerer Segmente.
       *
       * Unauth-User landen hier nie: der globale beforeEach unten
       * redirectet sie vorher nach /login (mit redirect-Param fuer
       * post-Login-Rueckkehr).
       */
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue')
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

/**
 * Pfade, die als „Default-Landing" gelten. Wenn ein nicht-eingeloggter
 * User auf einen dieser Pfade laeuft, redirected der Guard zur Login-
 * Seite ohne `?redirect=`-Param — der Login fuehrt nach Erfolg ohnehin
 * zur Wishlist, also waere `?redirect=/wishlist` nur Url-Laerm.
 *
 * Echte Tief-Links (z.B. `/admin/sparepart/<id>/edit`) bleiben weiterhin
 * mit Redirect-Param erhalten, sodass der User nach Login direkt am
 * urspruenglichen Ziel landet.
 */
const DEFAULT_LANDING_PATHS = new Set(['/', '/wishlist'])

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (auth.me === null && !to.meta.public) {
    try {
      await auth.fetchMe()
    } catch {
      /* netzwerkfehler ignorieren, unten unauthenticated-redirect */
    }
  }
  if (!auth.isAuthenticated && !to.meta.public) {
    return DEFAULT_LANDING_PATHS.has(to.fullPath)
      ? { name: 'login' }
      : { name: 'login', query: { redirect: to.fullPath } }
  }
  if (auth.isAuthenticated && to.meta.public) {
    return { name: 'wishlist' }
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'wishlist' }
  }
  if (to.meta.requiresManager && !auth.isManager) {
    return { name: 'wishlist' }
  }
  return true
})

export default router
